import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Info, Heart, PartyPopper } from "lucide-react";
import { apiFetch } from "@/api";
import { Button, Spinner, Field } from "@/ui";

const BENEFITS = [
  "One MAXx across Everyday, Learn, Create and Research",
  "Voice, images, files and live research with sources",
  "Their chats and memory, kept safe",
];

export default function PayForFriend() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const paid = params.get("paid") === "1";
  const sessionId = params.get("session_id");

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [sponsorName, setSponsorName] = useState("");
  const polled = useRef(false);

  useEffect(() => {
    (async () => {
      try { setInfo(await apiFetch(`/payments/sponsor/${code}`)); }
      catch (e) { setError(e.message || "This link isn't valid."); }
      finally { setLoading(false); }
    })();
  }, [code]);

  const poll = useCallback(async () => {
    for (let i = 0; i < 20; i++) {
      try {
        const s = await apiFetch(`/payments/status/${sessionId}`);
        if (s.payment_status === "paid") { setConfirmed(true); return; }
        if (["failed", "expired"].includes(s.payment_status)) return;
      } catch {}
      await new Promise((r) => setTimeout(r, 2000));
    }
  }, [sessionId]);

  useEffect(() => {
    if (paid && sessionId && !polled.current) { polled.current = true; poll(); }
  }, [paid, sessionId, poll]);

  const pay = async () => {
    setPaying(true);
    try {
      const d = await apiFetch("/payments/sponsor/checkout", { method: "POST", body: { code, origin_url: window.location.origin, sponsor_name: sponsorName.trim() || undefined } });
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      toast("Couldn't start checkout. Please try again.");
    } catch (e) { toast(e.message); } finally { setPaying(false); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center bg-background"><Spinner className="h-8 w-8 text-foreground" /></div>;

  if (error) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground">
      <p data-testid="pff-error" className="max-w-xs text-muted-foreground">{error}</p>
      <Button onClick={() => navigate("/")}>Go to MAXx</Button>
    </div>
  );

  if (paid) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background px-6 text-center text-foreground">
      {confirmed ? <PartyPopper className="h-12 w-12" strokeWidth={1.4} /> : <Spinner className="h-8 w-8 text-foreground" />}
      <h1 data-testid="pff-success" className="text-3xl font-bold tracking-tight">
        {confirmed ? `Thank you! ${info?.name || "Your friend"} now has MAXx Premium.` : "Confirming your payment…"}
      </h1>
      <p className="max-w-sm text-muted-foreground">
        {confirmed ? "It'll unlock on their account right away. You're a good friend. 💛" : "This only takes a moment."}
      </p>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-foreground">
      <div className="flex w-full max-w-md flex-col gap-7">
        <div className="flex flex-col gap-2">
          <Heart className="h-8 w-8" strokeWidth={1.4} />
          <h1 className="text-4xl font-bold tracking-tight">Cover MAXx for {info?.name || "a friend"}</h1>
          <p className="text-lg text-muted-foreground">
            {info?.already_active
              ? `${info?.name || "They"} already has MAXx Premium — you don't need to pay.`
              : `You're paying for ${info?.name || "your friend"}'s MAXx Premium — $20/month. Their account gets upgraded right after payment.`}
          </p>
        </div>

        {!info?.already_active ? (
          <>
            <div className="flex flex-col gap-3">
              {BENEFITS.map((b, i) => (
                <div key={i} className="flex items-center gap-3"><Check className="h-5 w-5" strokeWidth={1.5} /><span>{b}</span></div>
              ))}
            </div>
            <Field label="Your first name (optional)" value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} placeholder={`So ${info?.name || "they"} knows who to thank`} data-testid="pff-name" />
            <div className="flex gap-3 rounded-xl border border-border p-3 text-sm text-muted-foreground">
              <Info className="h-4 w-4 shrink-0" />
              <span data-testid="pff-consent">Please check with your parent or guardian before paying. Secure checkout by Stripe — in test mode use card <span className="font-semibold">4242 4242 4242 4242</span>, any future date and any CVC.</span>
            </div>
            <Button data-testid="pff-pay-btn" onClick={pay} loading={paying}>Pay $20/month for {info?.name || "them"}</Button>
          </>
        ) : (
          <Button data-testid="pff-home-btn" onClick={() => navigate("/")}>Go to MAXx</Button>
        )}
      </div>
    </div>
  );
}
